/* eslint-disable react-hooks/exhaustive-deps */
import React, { useContext, useState, useEffect, useMemo } from "react";
import { Link } from "react-router-dom";
import { APIContext } from "../../api/api";
import HelmetLayout from "../../components/HelmetLayout";
import { toast } from "react-toastify";
import { promiseAllInBatches } from "../../utils";
import { PulseLoader } from "react-spinners";
import {
  Palette,
  Search,
  Plus,
  RefreshCw,
  ExternalLink,
  Edit2,
  Filter,
} from "lucide-react";

export default function Themes() {
  const [themeList, setThemeList] = useState([]);
  const [loading, setLoading] = useState(true);
  const [isSyncingThemes, setIsSyncingThemes] = useState(false);
  const [syncingThemeId, setSyncingThemeId] = useState(null);
  const [searchQuery, setSearchQuery] = useState("");
  const [selectedCategory, setSelectedCategory] = useState("all");

  const { listOfAllThemes, syncTheme } = useContext(APIContext);

  useEffect(() => {
    listOfAllThemes()
      .then((themes) => {
        setThemeList(Array.isArray(themes) ? themes : []);
        setLoading(false);
      })
      .catch((error) => {
        console.error(error);
        toast.error("Failed to load themes");
        setThemeList([]);
        setLoading(false);
      });
  }, []);

  const syncSingleTheme = (themeId) => {
    if (syncingThemeId) return;
    setSyncingThemeId(themeId);
    syncTheme(themeId)
      .then(() => {
        toast.success("Theme synced successfully!");
      })
      .catch((err) => {
        console.error(err);
        toast.error("Theme sync failed.");
      })
      .finally(() => setSyncingThemeId(null));
  };

  const syncThemePromise = (id) => syncTheme(id);

  const syncAllThemes = () => {
    if (isSyncingThemes || !themeList?.length) return;
    setIsSyncingThemes(true);

    const ids = themeList.map((t) => t.pk.replace("theme-", ""));
    promiseAllInBatches(syncThemePromise, ids, 8)
      .then(() => {
        toast.success("All themes synced successfully!");
        setIsSyncingThemes(false);
      })
      .catch((err) => {
        console.error(err);
        toast.error("Some themes failed to sync.");
        setIsSyncingThemes(false);
      });
  };

  // Categories extraction
  const categories = useMemo(() => {
    const cats = new Set();
    themeList.forEach((theme) => {
      if (Array.isArray(theme.category)) {
        theme.category.forEach((c) => {
          const val = typeof c === "object" ? c.value || c.label : c;
          if (val) cats.add(val.toLowerCase());
        });
      } else if (typeof theme.category === "string" && theme.category.trim()) {
        cats.add(theme.category.toLowerCase());
      }
    });
    return Array.from(cats).sort();
  }, [themeList]);

  // Filtered themes
  const filteredThemes = useMemo(() => {
    return themeList.filter((theme) => {
      const name = (theme.theme_name || "").toLowerCase();
      const slug = (theme.theme_slug || "").toLowerCase();
      const q = searchQuery.toLowerCase().trim();
      const matchesSearch = !q || name.includes(q) || slug.includes(q);

      let matchesCategory = true;
      if (selectedCategory !== "all") {
        if (Array.isArray(theme.category)) {
          matchesCategory = theme.category.some((c) => {
            const val = typeof c === "object" ? c.value || c.label : c;
            return val?.toLowerCase() === selectedCategory;
          });
        } else if (typeof theme.category === "string") {
          matchesCategory = theme.category.toLowerCase() === selectedCategory;
        } else {
          matchesCategory = false;
        }
      }

      return matchesSearch && matchesCategory;
    });
  }, [themeList, searchQuery, selectedCategory]);

  return (
    <>
      <HelmetLayout title="Themes" />
      <div className="space-y-6 pb-12">
        {/* Header Section */}
        <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
          <div>
            <h1 className="text-2xl font-bold text-slate-900">Themes</h1>
            <p className="text-slate-500 text-sm mt-0.5">
              Explore, preview, sync, and manage website themes.
            </p>
          </div>
          <div className="flex items-center gap-3">
            <button
              onClick={syncAllThemes}
              disabled={isSyncingThemes || loading}
              className="bg-white hover:bg-slate-50 text-slate-700 border border-slate-200 px-4 py-2.5 rounded-lg text-sm font-medium transition-colors shadow-sm flex items-center gap-2 disabled:opacity-50"
            >
              <RefreshCw
                size={16}
                className={isSyncingThemes ? "animate-spin text-[#623ce7]" : "text-slate-500"}
              />
              {isSyncingThemes ? "Syncing Themes..." : "Sync All Themes"}
            </button>
            <Link
              to="/themes/add"
              className="bg-[#623ce7] hover:bg-[#5030bc] text-white px-4 py-2.5 rounded-lg text-sm font-medium transition-colors shadow-lg shadow-[#623ce7]/20 flex items-center gap-2"
            >
              <Plus size={16} />
              Add New Theme
            </Link>
          </div>
        </div>

        {/* KPI Cards */}
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <div className="bg-white p-5 rounded-xl border border-slate-200 shadow-sm flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-slate-500 mb-1">Total Themes</p>
              <h3 className="text-2xl font-bold text-slate-900">
                {loading ? "—" : themeList.length}
              </h3>
            </div>
            <div className="p-3 bg-[#623ce7]/10 text-[#623ce7] rounded-xl">
              <Palette size={22} />
            </div>
          </div>
          <div className="bg-white p-5 rounded-xl border border-slate-200 shadow-sm flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-slate-500 mb-1">Active Categories</p>
              <h3 className="text-2xl font-bold text-slate-900">
                {loading ? "—" : categories.length || "0"}
              </h3>
            </div>
            <div className="p-3 bg-emerald-50 text-emerald-600 rounded-xl">
              <Filter size={22} />
            </div>
          </div>
        </div>

        {/* Filter Bar */}
        <div className="bg-white rounded-xl border border-slate-200 p-4 shadow-sm flex flex-col sm:flex-row justify-between items-stretch sm:items-center gap-4">
          <div className="relative flex-1 max-w-md">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" size={18} />
            <input
              type="text"
              placeholder="Search themes by name or slug..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="w-full pl-10 pr-4 py-2 bg-slate-50 border border-slate-200 rounded-lg text-sm text-slate-900 placeholder-slate-400 focus:bg-white focus:ring-2 focus:ring-[#623ce7] focus:outline-none transition-all"
            />
          </div>

          {categories.length > 0 && (
            <div className="flex items-center gap-2 overflow-x-auto pb-1 sm:pb-0 scrollbar-hide">
              <button
                onClick={() => setSelectedCategory("all")}
                className={`px-3 py-1.5 rounded-lg text-xs font-medium whitespace-nowrap transition-colors ${
                  selectedCategory === "all"
                    ? "bg-[#623ce7] text-white"
                    : "bg-slate-100 text-slate-600 hover:bg-slate-200"
                }`}
              >
                All
              </button>
              {categories.map((cat) => (
                <button
                  key={cat}
                  onClick={() => setSelectedCategory(cat)}
                  className={`px-3 py-1.5 rounded-lg text-xs font-medium capitalize whitespace-nowrap transition-colors ${
                    selectedCategory === cat
                      ? "bg-[#623ce7] text-white"
                      : "bg-slate-100 text-slate-600 hover:bg-slate-200"
                  }`}
                >
                  {cat}
                </button>
              ))}
            </div>
          )}
        </div>

        {/* Themes Grid */}
        {loading ? (
          <div className="flex flex-col justify-center items-center py-24">
            <PulseLoader color="#623ce7" size={12} />
            <p className="text-slate-400 text-sm mt-3">Loading themes...</p>
          </div>
        ) : filteredThemes.length === 0 ? (
          <div className="bg-white rounded-xl border border-slate-200 p-12 text-center text-slate-500 shadow-sm">
            <Palette className="w-12 h-12 text-slate-300 mx-auto mb-3" />
            <p className="font-semibold text-slate-700 text-base">No themes found</p>
            <p className="text-sm text-slate-400 mt-1">
              {searchQuery ? "Try refining your search query." : "Click 'Add New Theme' to create one."}
            </p>
          </div>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
            {filteredThemes.map((theme) => {
              const themeID = theme.pk.replace("theme-", "");
              const isSyncingThis = syncingThemeId === themeID;

              return (
                <div
                  key={themeID}
                  className="group bg-white rounded-xl border border-slate-200 overflow-hidden hover:border-[#623ce7]/50 transition-all duration-300 hover:shadow-lg hover:shadow-[#623ce7]/5 flex flex-col"
                >
                  {/* Thumbnail & Hover Overlay */}
                  <div className="relative h-48 bg-slate-100 overflow-hidden">
                    <img
                      src={theme.theme_thumbnail || "https://picsum.photos/seed/placeholder/400/250"}
                      alt={theme.theme_name}
                      className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
                      onError={(e) => {
                        e.target.src = "https://picsum.photos/seed/placeholder/400/250";
                      }}
                    />
                    <div className="absolute inset-0 bg-slate-900/60 opacity-0 group-hover:opacity-100 transition-opacity duration-200 flex items-center justify-center gap-3">
                      <a
                        href={`https://${theme.theme_slug}.grigora.app`}
                        target="_blank"
                        rel="noreferrer"
                        className="p-2.5 bg-white rounded-full text-slate-900 hover:bg-slate-100 transition-colors shadow-md"
                        title="Preview Live Site"
                      >
                        <ExternalLink size={18} />
                      </a>
                      <Link
                        to={`/themes/${themeID}`}
                        className="p-2.5 bg-[#623ce7] rounded-full text-white hover:bg-[#5030bc] transition-colors shadow-md"
                        title="Edit Theme"
                      >
                        <Edit2 size={18} />
                      </Link>
                    </div>

                    <div className="absolute top-3 right-3 bg-white/90 backdrop-blur-sm px-2.5 py-1 rounded-md text-xs font-mono text-slate-700 font-medium shadow-sm border border-slate-100">
                      {theme.theme_slug}
                    </div>
                  </div>

                  {/* Card Content */}
                  <div className="p-5 flex-1 flex flex-col justify-between">
                    <div>
                      <div className="flex justify-between items-start mb-1">
                        <Link
                          to={`/themes/${themeID}`}
                          className="font-semibold text-slate-900 hover:text-[#623ce7] transition-colors text-base truncate block"
                          title={theme.theme_name}
                        >
                          {theme.theme_name}
                        </Link>
                      </div>
                      <p className="text-xs text-slate-400 font-mono mb-3">
                        {theme.theme_slug}.grigora.app
                      </p>

                      {/* Categories / Tags */}
                      {Array.isArray(theme.category) && theme.category.length > 0 && (
                        <div className="flex flex-wrap gap-1.5 mb-4">
                          {theme.category.slice(0, 3).map((c, i) => {
                            const label = typeof c === "object" ? c.label || c.value : c;
                            return (
                              <span
                                key={i}
                                className="px-2 py-0.5 bg-slate-100 rounded text-[11px] font-medium text-slate-600 border border-slate-200 capitalize"
                              >
                                {label}
                              </span>
                            );
                          })}
                        </div>
                      )}
                    </div>

                    {/* Bottom Actions */}
                    <div className="pt-3 border-t border-slate-100 flex items-center justify-between gap-2 mt-auto">
                      <button
                        onClick={() => syncSingleTheme(themeID)}
                        disabled={isSyncingThis}
                        className="text-xs font-medium text-slate-600 hover:text-[#623ce7] flex items-center gap-1.5 transition-colors disabled:opacity-50"
                      >
                        <RefreshCw
                          size={13}
                          className={isSyncingThis ? "animate-spin text-[#623ce7]" : ""}
                        />
                        {isSyncingThis ? "Syncing..." : "Sync"}
                      </button>

                      <div className="flex items-center gap-2">
                        <a
                          href={`https://${theme.theme_slug}.grigora.app`}
                          target="_blank"
                          rel="noreferrer"
                          className="text-xs text-slate-500 hover:text-slate-800 flex items-center gap-1 transition-colors"
                        >
                          Live Preview <ExternalLink size={12} />
                        </a>
                        <Link
                          to={`/themes/${themeID}`}
                          className="text-xs font-medium bg-[#623ce7]/10 hover:bg-[#623ce7]/20 text-[#623ce7] px-2.5 py-1 rounded transition-colors"
                        >
                          Edit
                        </Link>
                      </div>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </>
  );
}
